import { prepaidCards } from '@huggg/back-bar';
import initialStateLoader from '../initialState';

const SUBMIT_FORM = 'huggg/prepaid-cards/SUBMIT_FORM';

const GENERATE_OTP = 'huggg/prepaid-cards/GENERATE_OTP';
const GENERATE_OTP_SUCCESS = 'huggg/prepaid-cards/GENERATE_OTP_SUCCESS';
const GENERATE_OTP_ERROR = 'huggg/prepaid-cards/GENERATE_OTP_ERROR';

const FETCH_CARD_DATA = 'huggg/prepaid-cards/FETCH_CARD_DATA';
const FETCH_CARD_DATA_SUCCESS = 'huggg/prepaid-cards/FETCH_CARD_DATA_SUCCESS';
const FETCH_CARD_DATA_ERROR = 'huggg/prepaid-cards/FETCH_CARD_DATA_ERROR';

const FETCH_SENSITIVE_CARD_DATA =
  'huggg/prepaid-cards/FETCH_SENSITIVE_CARD_DATA';
const FETCH_SENSITIVE_CARD_DATA_SUCCESS =
  'huggg/prepaid-cards/FETCH_SENSITIVE_CARD_DATA_SUCCESS';
const FETCH_SENSITIVE_CARD_DATA_ERROR =
  'huggg/prepaid-cards/FETCH_SENSITIVE_CARD_DATA_ERROR';

const REPORT_MISUSE = 'huggg/prepaid-cards/REPORT_MISUSE';
const REPORT_MISUSE_SUCCESS = 'huggg/prepaid-cards/REPORT_MISUSE_SUCCESS';
const REPORT_MISUSE_ERROR = 'huggg/prepaid-cards/REPORT_MISUSE_ERROR';

const OTP_TIMER_START = 'huggg/prepaid-cards/OTP_TIMER_START';
const OTP_TIMER_STOP = 'huggg/prepaid-cards/OTP_TIMER_STOP';
const OTP_TIMER_TICK = 'huggg/prepaid-cards/OTP_TIMER_TICK';

const STATUS_POLL_START = 'huggg/prepaid-cards/STATUS_POLL_START';
const STATUS_POLL_SUCCESS = 'huggg/prepaid-cards/STATUS_POLL_SUCCESS';
const STATUS_POLL_FAILURE = 'huggg/prepaid-cards/STATUS_POLL_FAILURE';
const STATUS_POLL_STOP = 'huggg/prepaid-cards/STATUS_POLL_STOP';
const STATUS_POLL_EXHAUSTED = 'huggg/prepaid-cards/STATUS_POLL_EXHAUSTED';

export const types = {
  STATUS_POLL_START,
  STATUS_POLL_SUCCESS,
  STATUS_POLL_FAILURE,
  STATUS_POLL_STOP,
  STATUS_POLL_EXHAUSTED,
};

const OTP_DELAY_SEC = 30;

// Card creation runs on a capped provider queue and usually takes seconds.
// Poll for a minute, then tell the recipient to check back rather than hold
// a spinner: the backend retries on its own and a refresh starts afresh.
export const STATUS_POLL_INTERVAL_MS = 5000;
export const STATUS_POLL_CEILING_MS = 60 * 1000;

// A status is settled when there is nothing left to wait for: the provider
// refused (creationFailure), or the card is out of creation and funding is no
// longer pending (funded, failed, or a legacy card that reads as funded).
export const isCardStatusSettled = (status) => {
  if (!status) {
    return false;
  }

  return (
    Boolean(status.creationFailure) ||
    (status.status !== 'pending-creation' && status.fundingStatus !== 'pending')
  );
};

const { prepaidCard: initialState } = initialStateLoader();

/* Reducer - Default Export
========================================================================== */
const reducer = (state = initialState, action) => {
  const { payload, type } = action;

  switch (type) {
    case SUBMIT_FORM:
      return { ...state, formData: payload.formData };

    case OTP_TIMER_START:
      return {
        ...state,
        otpDelay: OTP_DELAY_SEC,
      };

    case OTP_TIMER_STOP:
      return {
        ...state,
        otpDelay: 0,
      };

    case OTP_TIMER_TICK:
      return {
        ...state,
        otpDelay: Math.max(state.otpDelay - 1, 0),
      };

    case FETCH_CARD_DATA:
      return {
        ...state,
        fetchCardDataError: null,
        isFetchingCardData: true,
      };

    case FETCH_CARD_DATA_SUCCESS:
      return {
        ...state,
        fetchCardDataError: null,
        isFetchingCardData: false,
        cardData: payload.cardData,
        // A polled status is fresher than the card only while the poll runs and
        // only for the same card; once settled, the card is the truth.
        cardStatus:
          state.isPollingStatus &&
          state.cardStatus &&
          state.cardStatus.id === payload.cardData?.id
            ? state.cardStatus
            : null,
      };

    case FETCH_CARD_DATA_ERROR:
      return {
        ...state,
        fetchCardDataError: payload.error,
        isFetchingCardData: false,
      };

    case STATUS_POLL_START:
      return {
        ...state,
        cardStatus: null,
        statusPollError: null,
        isPollingStatus: true,
        isStatusPollExhausted: false,
      };

    case STATUS_POLL_SUCCESS:
      return {
        ...state,
        cardStatus: payload.cardStatus,
        statusPollError: null,
        isPollingStatus: !isCardStatusSettled(payload.cardStatus),
      };

    case STATUS_POLL_FAILURE:
      return {
        ...state,
        statusPollError: payload.error,
      };

    case STATUS_POLL_STOP:
      return {
        ...state,
        isPollingStatus: false,
      };

    case STATUS_POLL_EXHAUSTED:
      return {
        ...state,
        isPollingStatus: false,
        isStatusPollExhausted: true,
      };

    case FETCH_SENSITIVE_CARD_DATA:
      return {
        ...state,
        fetchSensitiveCardDataError: null,
        isFetchingSensitiveCardData: true,
      };

    case FETCH_SENSITIVE_CARD_DATA_SUCCESS:
      return {
        ...state,
        fetchSensitiveCardDataError: null,
        isFetchingSensitiveCardData: false,
        sensitiveCardData: payload.sensitiveCardData,
      };

    case FETCH_SENSITIVE_CARD_DATA_ERROR:
      return {
        ...state,
        fetchSensitiveCardDataError: payload.error,
        isFetchingSensitiveCardData: false,
      };

    case REPORT_MISUSE:
      return {
        ...state,
        reportMisuseError: null,
        isReportingMisuse: true,
      };

    case REPORT_MISUSE_SUCCESS:
      return {
        ...state,
        reportMisuseError: null,
        isReportingMisuse: false,
      };

    case REPORT_MISUSE_ERROR:
      return {
        ...state,
        reportMisuseError: payload.error,
        isReportingMisuse: false,
      };

    case GENERATE_OTP:
      return {
        ...state,
        generatingOtpError: null,
        loading: true,
      };

    case GENERATE_OTP_SUCCESS:
      return {
        ...state,
        generatingOtpError: null,
        loading: false,
      };

    case GENERATE_OTP_ERROR:
      return {
        ...state,
        loading: false,
        generatingOtpError: payload.error,
      };

    default:
      return state;
  }
};

export default reducer;

/* Side Effects
========================================================================== */

export const generateOTP = (otpEmailTemplateName) => (dispatch, getState) => {
  const delayResend = () => {
    let timer = null;
    dispatch({ type: OTP_TIMER_START });
    clearInterval(timer);
    timer = setInterval(() => {
      dispatch({ type: OTP_TIMER_TICK });
      const { otpDelay } = getState().prepaidCard;
      if (otpDelay <= 0) {
        clearInterval(timer);
        dispatch({ type: OTP_TIMER_STOP });
      }
    }, 1000);
  };

  const { formData } = getState().prepaidCard;
  dispatch({ type: GENERATE_OTP });
  delayResend();
  prepaidCards
    .generateOTP(formData.email, otpEmailTemplateName)
    .then(() => {
      dispatch({ type: GENERATE_OTP_SUCCESS });
    })
    .catch((err) => {
      if (process.env.NODE_ENV === 'development' && !window.Cypress) {
        console.error(err);
      }
      dispatch({
        type: GENERATE_OTP_ERROR,
        payload: { error: err.response.data },
      });
    });
};

export const fetchCardData = (id) => (dispatch, getState) => {
  const { isFetchingCardData } = getState().prepaidCard;

  if (isFetchingCardData) {
    return;
  }

  dispatch({ type: FETCH_CARD_DATA });

  prepaidCards
    .getById(id)
    .then((data) => {
      dispatch({ type: FETCH_CARD_DATA_SUCCESS, payload: { cardData: data } });
    })
    .catch((err) => {
      if (process.env.NODE_ENV === 'development' && !window.Cypress) {
        console.error(err);
      }
      dispatch({
        type: FETCH_CARD_DATA_ERROR,
        payload: { error: err.response.data },
      });
    });
};

// Module-scoped timer so unmount/stop can cancel the loop without putting a
// timer in Redux state. The run token makes a late response a no-op.
let statusPollTimer = null;
let statusPollRun = 0;

const stopStatusTimer = () => {
  if (statusPollTimer) {
    clearTimeout(statusPollTimer);
    statusPollTimer = null;
  }
};

const scheduleNextStatusPoll = (id, dispatch, getState, startedAt, run) => {
  const elapsedMs = Date.now() - startedAt;

  if (elapsedMs >= STATUS_POLL_CEILING_MS) {
    statusPollTimer = null;
    dispatch({ type: STATUS_POLL_EXHAUSTED });
    return;
  }

  statusPollTimer = setTimeout(
    () => pollStatusOnce(id, dispatch, getState, startedAt, run),
    STATUS_POLL_INTERVAL_MS,
  );
};

const pollStatusOnce = (id, dispatch, getState, startedAt, run) =>
  prepaidCards
    .getStatusById(id)
    .then((cardStatus) => {
      if (run !== statusPollRun) {
        return cardStatus;
      }

      if (!isCardStatusSettled(cardStatus)) {
        dispatch({ type: STATUS_POLL_SUCCESS, payload: { cardStatus } });
        scheduleNextStatusPoll(id, dispatch, getState, startedAt, run);
        return cardStatus;
      }

      // A refusal renders from the status alone, so report it at once rather
      // than behind the by-id fetch, where a 429 could hide it until the ceiling.
      if (cardStatus.creationFailure) {
        statusPollTimer = null;
        dispatch({ type: STATUS_POLL_SUCCESS, payload: { cardStatus } });
        return cardStatus;
      }

      // A ready view renders balance, digits and expiry from the full card, so the
      // refresh is part of settling: report settled only once the card is fetched.
      // A failed fetch is transient and the next tick retries.
      return prepaidCards.getById(id).then((cardData) => {
        if (run !== statusPollRun) {
          return cardStatus;
        }

        statusPollTimer = null;
        dispatch({ type: FETCH_CARD_DATA_SUCCESS, payload: { cardData } });
        dispatch({ type: STATUS_POLL_SUCCESS, payload: { cardStatus } });
        return cardStatus;
      });
    })
    .catch((err) => {
      if (run !== statusPollRun) {
        return;
      }

      if (process.env.NODE_ENV === 'development' && !window.Cypress) {
        console.error(err);
      }
      dispatch({
        type: STATUS_POLL_FAILURE,
        payload: { error: err?.response?.data ?? err },
      });
      // A transient failure does not end the wait: try again next tick.
      scheduleNextStatusPoll(id, dispatch, getState, startedAt, run);
    });

export const startStatusPolling = (id) => (dispatch, getState) => {
  stopStatusTimer();
  statusPollRun += 1;
  dispatch({ type: STATUS_POLL_START });
  return pollStatusOnce(id, dispatch, getState, Date.now(), statusPollRun);
};

export const stopStatusPolling = () => (dispatch) => {
  stopStatusTimer();
  statusPollRun += 1;
  dispatch({ type: STATUS_POLL_STOP });
};

export const fetchSensitiveCardData = () => (dispatch, getState) => {
  const { cardData, isFetchingSensitiveCardData } = getState().prepaidCard;

  if (isFetchingSensitiveCardData || !cardData.id) {
    return;
  }

  dispatch({ type: FETCH_SENSITIVE_CARD_DATA });

  prepaidCards
    .getSensitiveCardDetails(cardData.id)
    .then((data) => {
      dispatch({
        type: FETCH_SENSITIVE_CARD_DATA_SUCCESS,
        payload: { sensitiveCardData: data },
      });
    })
    .catch((err) => {
      if (process.env.NODE_ENV === 'development' && !window.Cypress) {
        console.error(err);
      }
      dispatch({
        type: FETCH_SENSITIVE_CARD_DATA_ERROR,
        payload: { error: err.response.data },
      });
    });
};

export const reportMisuse = (status) => (dispatch, getState) => {
  const { cardData, isReportingMisuse } = getState().prepaidCard;

  if (isReportingMisuse || !cardData.id) {
    return;
  }

  dispatch({ type: REPORT_MISUSE });

  prepaidCards
    .reportMisuse(cardData.id, status)
    .then(() => {
      dispatch({ type: REPORT_MISUSE_SUCCESS });

      fetchCardData(cardData.id)(dispatch, getState);
    })
    .catch((err) => {
      if (process.env.NODE_ENV === 'development' && !window.Cypress) {
        console.error(err);
      }
      dispatch({
        type: REPORT_MISUSE_ERROR,
        payload: { error: err.response.data },
      });
    });
};

/* Actions
========================================================================== */
export const submitForm = (formData) => (dispatch) =>
  dispatch({ type: SUBMIT_FORM, payload: { formData } });

/* Selectors
========================================================================== */
export const getIsLoading = (state) => state.prepaidCard.loading;
export const getFormData = (state) => state.prepaidCard.formData;
export const getGeneratingOtpError = (state) =>
  state.prepaidCard.generatingOtpError;
export const getCardData = (state) => state.prepaidCard.cardData;
export const getIsFetchingCardData = (state) =>
  state.prepaidCard.isFetchingCardData;
export const getFetchCardDataError = (state) =>
  state.prepaidCard.fetchCardDataError;
export const getCardStatus = (state) => state.prepaidCard.cardStatus;
export const getIsStatusPollExhausted = (state) =>
  state.prepaidCard.isStatusPollExhausted;
export const getSensitiveCardData = (state) =>
  state.prepaidCard.sensitiveCardData;
export const getIsFetchingSensitiveCardData = (state) =>
  state.prepaidCard.isFetchingSensitiveCardData;
export const getFetchSensitiveCardDataError = (state) =>
  state.prepaidCard.fetchSensitiveCardDataError;
export const getIsReportingMisuse = (state) =>
  state.prepaidCard.isReportingMisuse;
export const getReportMisuseError = (state) =>
  state.prepaidCard.reportMisuseError;
export const getAvailableBalanceStr = (state) => {
  const { currency = 'GBP', availableBalance } =
    state.prepaidCard.cardData || {};

  if (!availableBalance) {
    return (0).toLocaleString(undefined, { currency, style: 'currency' });
  }

  return (availableBalance / 100).toLocaleString(undefined, {
    currency,
    style: 'currency',
  });
};
export const getSpentAmountStr = (state) => {
  const {
    currency = 'GBP',
    availableBalance,
    issuedAmount,
    fundingStatus,
  } = state.prepaidCard.cardData || {};

  // An unfunded card's zero balance is missing funds, not spend: deriving
  // issuedAmount - availableBalance would fabricate "Amount used".
  const spentAmount =
    fundingStatus === 'pending' || fundingStatus === 'failed'
      ? 0
      : issuedAmount - availableBalance;

  if (!spentAmount) {
    return (0).toLocaleString(undefined, { currency, style: 'currency' });
  }

  return (spentAmount / 100).toLocaleString(undefined, {
    currency,
    style: 'currency',
  });
};
export const getOtpDelay = (state) => state.prepaidCard.otpDelay;
export const actions = {
  submitForm,
  generateOTP,
  fetchCardData,
  fetchSensitiveCardData,
  reportMisuse,
  startStatusPolling,
  stopStatusPolling,
};
